import { useEffect, useState } from "react";
import { X, PaperPlaneTilt, CheckCircle, Envelope, Phone, Buildings } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useUser } from "../lib/UserContext";

export default function MessageComposer({
  open,
  onClose,
  brand,
  product,
  complaint = null,
  onSent,
}) {
  const { user } = useUser();
  const [contact, setContact] = useState(null);
  const [sender, setSender] = useState({ name: "", email: "" });
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDone(false);
    setError("");
    setSender({
      name: user?.name || "",
      email: user?.email || "",
    });
    // Pre-fill subject & body based on context
    if (complaint) {
      setSubject(`Consumer complaint — ${complaint.category.replace("_", " ")} — ${brand}`);
      setBody(
        [
          `Hello ${contact?.contact_name || "Regulatory Affairs"},`,
          "",
          `A ${complaint.severity} severity complaint was flagged on Cosmetovigilance by GCRS for:`,
          `• Product: ${product?.name || complaint.product_name}`,
          `• Region: ${complaint.region}`,
          `• Category: ${complaint.category.replace("_", " ")}`,
          `• Source: ${complaint.source}`,
          "",
          `Consumer report excerpt:`,
          `"${complaint.text}"`,
          "",
          "Please review and acknowledge within your usual SLA.",
          "",
          "— GCRS Cosmetovigilance Team",
        ].join("\n")
      );
    } else if (product) {
      setSubject(`Complaint signals review — ${product.name}`);
      setBody(
        [
          `Hello ${contact?.contact_name || "Regulatory Affairs"},`,
          "",
          `We are reaching out regarding the aggregate consumer signals currently being tracked for ${brand} — ${product.name}.`,
          "",
          "Please confirm receipt and let us know the best point of contact for follow-up.",
          "",
          "— GCRS Cosmetovigilance Team",
        ].join("\n")
      );
    } else {
      setSubject(`GCRS Cosmetovigilance — ${brand}`);
      setBody("");
    }
    (async () => {
      try {
        const c = await api.getBrand(brand);
        setContact(c);
      } catch {
        setContact(null);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, brand, product?.id, complaint?.id]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!sender.name || !sender.email) {
      setError("Your name and email are required.");
      return;
    }
    setLoading(true);
    try {
      const msg = await api.sendTeamMessage({
        brand,
        product_id: product?.id || null,
        complaint_id: complaint?.id || null,
        subject,
        body,
        sender_name: sender.name,
        sender_email: sender.email,
      });
      setDone(true);
      onSent?.(msg);
      setTimeout(onClose, 1400);
    } catch (err) {
      setError(err?.response?.data?.detail?.toString() || "Send failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="message-composer-modal"
      className="fixed inset-0 z-50 bg-[#1C1B1A]/50 backdrop-blur-sm flex items-start justify-center pt-[8vh] px-4"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-2xl bg-white border border-[#1C1B1A] max-h-[86vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E2DC]">
          <div>
            <div className="eyebrow">Internal team → Brand</div>
            <div className="font-heading text-xl mt-1">Direct message to {brand}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="message-close"
            className="w-8 h-8 flex items-center justify-center text-[#6E6B65] hover:text-[#1C1B1A]"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div className="p-10 text-center" data-testid="message-success">
            <CheckCircle size={44} className="mx-auto text-[#40593B]" weight="duotone" />
            <div className="font-heading text-2xl mt-4">Message queued.</div>
            <div className="text-sm text-[#6E6B65] mt-1">
              Delivered to {contact?.email || brand}. Tracked in the Messages inbox.
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="flex-1 overflow-y-auto thin-scroll p-6 space-y-4">
            {contact && (
              <div className="border border-[#E5E2DC] bg-[#F7F5F0] p-4" data-testid="contact-card">
                <div className="eyebrow mb-2 flex items-center gap-1.5">
                  <Buildings size={11} /> Brand contact
                </div>
                <div className="font-heading font-medium text-[15px]">{contact.contact_name}</div>
                <div className="text-[12px] text-[#6E6B65]">
                  HQ — {contact.hq_country} · {contact.hq_region}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[#1C1B1A]">
                  <span className="flex items-center gap-1">
                    <Envelope size={11} className="text-[#A66E4E]" /> {contact.email}
                  </span>
                  <span className="flex items-center gap-1">
                    <Phone size={11} className="text-[#A66E4E]" /> {contact.phone}
                  </span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="eyebrow block mb-1.5">Your name</label>
                <input
                  data-testid="msg-sender-name"
                  value={sender.name}
                  onChange={(e) => setSender({ ...sender, name: e.target.value })}
                  className="w-full px-3 py-2.5 border border-[#E5E2DC] focus:border-[#A66E4E] focus:outline-none focus:ring-1 focus:ring-[#A66E4E] bg-white font-heading text-[15px]"
                  placeholder="GCRS Analyst"
                />
              </div>
              <div>
                <label className="eyebrow block mb-1.5">Your email</label>
                <input
                  data-testid="msg-sender-email"
                  type="email"
                  value={sender.email}
                  onChange={(e) => setSender({ ...sender, email: e.target.value })}
                  className="w-full px-3 py-2.5 border border-[#E5E2DC] focus:border-[#A66E4E] focus:outline-none focus:ring-1 focus:ring-[#A66E4E] bg-white font-heading text-[15px]"
                  placeholder="team@gcrs.example"
                />
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Subject</label>
              <input
                data-testid="msg-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E5E2DC] focus:border-[#A66E4E] focus:outline-none focus:ring-1 focus:ring-[#A66E4E] bg-white font-heading text-[15px]"
              />
            </div>
            <div>
              <label className="eyebrow block mb-1.5">Message</label>
              <textarea
                data-testid="msg-body"
                rows={10}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                className="w-full px-3 py-2.5 border border-[#E5E2DC] focus:border-[#A66E4E] focus:outline-none focus:ring-1 focus:ring-[#A66E4E] bg-white font-body text-[13.5px] leading-relaxed resize-none"
              />
            </div>

            {error && (
              <div className="text-[12px] text-[#D93636] bg-[#FBEAE9] px-3 py-2" data-testid="msg-error">
                {error}
              </div>
            )}

            <div className="flex items-center justify-between gap-3 pt-2">
              <p className="text-[11px] text-[#6E6B65] leading-relaxed flex-1">
                Delivery status is tracked in the Messages inbox. <strong>MOCKED:</strong> outbound
                email gateway (e.g., Resend) is not wired yet — messages are persisted to the
                GCRS audit log.
              </p>
              <button
                type="submit"
                disabled={loading}
                data-testid="msg-send-btn"
                className="bg-[#1C1B1A] text-white px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-[#A66E4E] transition-colors flex items-center gap-2 disabled:opacity-60"
              >
                <PaperPlaneTilt size={13} weight="bold" />
                {loading ? "Sending…" : "Send to brand"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
